import { useCallback, useEffect, useRef, useState } from 'react';
import type { FilterState } from '../components/FilterDrawer';
import { useClientScope } from '../contexts';

/**
 * Filter keys that identify client-specific org entities (region/depot/
 * department/job-role ids, and selected user ids). These ids are only valid
 * for the client they were selected under — switching client leaves them
 * pointing at the wrong org structure (e.g. a department id that happens to
 * exist for a different client, with a different name). ageRange/sex/
 * shiftPattern are fixed, client-independent vocabularies and survive a
 * client switch unchanged.
 */
const CLIENT_SCOPED_KEYS: Array<'region' | 'depot' | 'department' | 'jobRole' | 'searchUser'> = [
  'region',
  'depot',
  'department',
  'jobRole',
  'searchUser',
];

const STORAGE_KEY = 'peak4.admin.filters.v1';

const DEFAULT_FILTERS: FilterState = {
  region: [],
  depot: [],
  department: [],
  jobRole: [],
  searchUser: [],
  ageRange: [],
  sex: [],
  shiftPattern: [],
  reliableDataOnly: false,
};

const toStringArray = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === 'string');
};

const loadFilters = (key: string): FilterState | null => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<FilterState> | null;
    if (!parsed || typeof parsed !== 'object') return null;

    return {
      region: toStringArray(parsed.region),
      depot: toStringArray(parsed.depot),
      department: toStringArray(parsed.department),
      jobRole: toStringArray(parsed.jobRole),
      searchUser: toStringArray(parsed.searchUser),
      ageRange: toStringArray(parsed.ageRange),
      sex: toStringArray(parsed.sex),
      shiftPattern: toStringArray(parsed.shiftPattern),
      // Absent from filters persisted before BT-101, so default to off rather
      // than silently narrowing an existing user's dashboards.
      reliableDataOnly: parsed.reliableDataOnly === true,
    };
  } catch {
    return null;
  }
};

export const usePersistedFilters = (options?: { storageKey?: string; defaultFilters?: FilterState }) => {
  const storageKey = options?.storageKey ?? STORAGE_KEY;
  const fallbackFilters = options?.defaultFilters ?? DEFAULT_FILTERS;
  const { effectiveClientId } = useClientScope();

  const [filters, setFilters] = useState<FilterState>(() => {
    return loadFilters(storageKey) ?? fallbackFilters;
  });
  const [filtersResetForClient, setFiltersResetForClient] = useState(false);
  const previousClientIdRef = useRef(effectiveClientId);

  // PEAK-266: region/depot/department/job-role/searchUser are ids scoped to
  // whichever client they were picked under (e.g. a Cadent department id).
  // Switching client leaves those stale ids selected — they may coincide
  // with a different, unrelated entity for the new client (wrong label) or
  // match nothing at all — so clear them whenever the effective client
  // actually changes.
  useEffect(() => {
    if (previousClientIdRef.current === effectiveClientId) return;
    previousClientIdRef.current = effectiveClientId;

    setFilters((prev) => {
      const hasClientScopedSelection = CLIENT_SCOPED_KEYS.some((key) => prev[key].length > 0);
      if (!hasClientScopedSelection) return prev;

      setFiltersResetForClient(true);
      const reset = { ...prev };
      for (const key of CLIENT_SCOPED_KEYS) reset[key] = [];
      return reset;
    });
  }, [effectiveClientId]);

  const acknowledgeFiltersReset = useCallback(() => setFiltersResetForClient(false), []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(filters));
    } catch {
      // ignore storage errors (e.g. disabled cookies/storage)
    }
  }, [filters, storageKey]);

  return { filters, setFilters, filtersResetForClient, acknowledgeFiltersReset } as const;
};
